'use client'

import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { LockIcon, SaveIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { PasswordInput } from '@/components/ui/password-input'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useResetPassword } from '@/hooks/use-auth'
import { getErrorMessage, setValidationErrors } from '@/lib/utils'

type PropsType = {
  token: string
}

const ResetPasswordForm = ({ token }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const { mutate, isPending } = useResetPassword(token)

  const formSchema = z
    .object({
      password: z
        .string()
        .min(6, t('min_value', { value: 6 }))
        .max(32, t('max_value', { value: 32 })),
      passwordConfirm: z
        .string()
        .min(6, t('min_value', { value: 6 }))
        .max(32, t('max_value', { value: 32 }))
    })
    .refine(data => data.password === data.passwordConfirm, {
      message: t('password_mismatch'),
      path: ['passwordConfirm']
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '', passwordConfirm: '' }
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    mutate(data, {
      onSuccess: () => {
        toast.success(t('password_reset_success'))
        router.push('/login')
      },
      onError: (error: any) => {
        if (error.response?.status === 422) {
          setValidationErrors(error, form.setError)
        } else {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
        }
      }
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="reset-password" required>
                  {t('new_password')}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    id="reset-password"
                    autoComplete="new-password"
                    placeholder={t('enter_new_password')}
                    icon={<LockIcon className="h-4 w-4" />}
                    autoFocus
                    disabled={isPending}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirm"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="reset-password-confirm" required>
                  {t('password_confirm')}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    id="reset-password-confirm"
                    autoComplete="new-password"
                    placeholder={t('confirm_password')}
                    icon={<LockIcon className="h-4 w-4" />}
                    disabled={isPending}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isPending} loading={isPending}>
            <SaveIcon className="h-4 w-4" />
            {t('reset_password')}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default ResetPasswordForm
